import { createSelector, createSlice } from "@reduxjs/toolkit";
import { takeDose, fetchMedications, fetchActiveMedications, fetchMedicationHistory, fetchArchivedMedications, archiveMedication, checkArchive, toggleAlarm, deleteMedication } from "../thunks/medicationThunks";

const normalizeMedicationList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.medications)) return payload.medications;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getActiveMedications = (payload) =>
  normalizeMedicationList(payload).filter((medication) => !medication?.isArchived);

const initialState = {
  medications: [],
  activeMedications: [],
  medicationHistory: [],
  archivedMedications: [],
  reminder: null,
  isLoading: false,
  isError: false,
  errorMessage: null,
  stockReminder: null,
};

const medicationSlice = createSlice({
  name: "medication",
  initialState,
  reducers: {
    reminderReceived: (state, action) => {
      state.reminder = action.payload;
    },
    stockReminderReceived: (state, action) => {
      state.stockReminder = action.payload;
    },
    clearReminder: (state) => {
      state.reminder = null;
    },
    dismissReminder: (state) => {
      state.reminder = null;
    },
    resetMedicationState: () => initialState,
    setMedications: (state, action) => {
      const medications = normalizeMedicationList(action.payload);
      state.medications = medications;
      state.activeMedications = medications.filter((medication) => !medication?.isArchived);
      state.isLoading = false;
    },
    setActiveMedications: (state, action) => {
      state.activeMedications = action.payload;
    },
    setMedicationHistory: (state, action) => {
      state.medicationHistory = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.isError = true;
      state.errorMessage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(takeDose.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(takeDose.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(takeDose.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(fetchMedications.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
     .addCase(fetchMedications.fulfilled, (state, action) => {
        const data = Array.isArray(action.payload) ? action.payload : [];


        state.medications = data;
        state.activeMedications = data;
        state.isLoading = false;
      })
      .addCase(fetchMedications.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(fetchActiveMedications.pending, (state) => {
  state.isLoading = true;
})
.addCase(fetchActiveMedications.fulfilled, (state, action) => {
  state.isLoading = false;

  const data = Array.isArray(action.payload) ? action.payload : [];

  state.activeMedications = data;
})
.addCase(fetchActiveMedications.rejected, (state, action) => {
  state.isLoading = false;
  state.isError = true;
  state.errorMessage = action.payload;
})
      .addCase(fetchMedicationHistory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchMedicationHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.medicationHistory = action.payload;
      })
      .addCase(fetchMedicationHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(fetchArchivedMedications.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchArchivedMedications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.archivedMedications = action.payload;
      })
      .addCase(fetchArchivedMedications.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(archiveMedication.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(archiveMedication.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove from active medications if present
        const medicationId = action.meta.arg.id;
        state.medications = state.medications.filter((m) => m.id !== medicationId);
        state.activeMedications = state.activeMedications.filter((m) => m.id !== medicationId);
        // Add to archived medications
        if (action.payload) {
          state.archivedMedications.push(action.payload);
        }
      })
      .addCase(archiveMedication.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(checkArchive.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkArchive.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update archived medications list if returned
        if (action.payload) {
          state.archivedMedications = action.payload;
        }
      })
      .addCase(checkArchive.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(toggleAlarm.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(toggleAlarm.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the medication in the list if returned
        if (action.payload) {
          const medicationId = action.meta.arg.id;
          const medIndex = state.medications.findIndex((m) => m.id === medicationId);
          if (medIndex !== -1) {
            state.medications[medIndex] = action.payload;
          }
          const activeMedIndex = state.activeMedications.findIndex((m) => m.id === medicationId);
          if (activeMedIndex !== -1) {
            state.activeMedications[activeMedIndex] = action.payload;
          }
        }
      })
      .addCase(toggleAlarm.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(deleteMedication.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteMedication.fulfilled, (state, action) => {
        state.isLoading = false;
        const medicationId = action.payload.id;
        // Remove from all lists
        state.medications = state.medications.filter((m) => m.id !== medicationId);
        state.activeMedications = state.activeMedications.filter((m) => m.id !== medicationId);
        state.medicationHistory = state.medicationHistory.filter((m) => m.id !== medicationId);
        state.archivedMedications = state.archivedMedications.filter((m) => m.id !== medicationId);
      })
      .addCase(deleteMedication.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      });
  },
});

export const {
  reminderReceived,
  clearReminder,
  dismissReminder,
  resetMedicationState,
  setMedications,
  setActiveMedications,
  setMedicationHistory,
  setLoading,
  setError,
  stockReminderReceived,
  clearStockReminder,
} = medicationSlice.actions;

/* ⚠️ Le reducer est monté sous `medications` dans le store (store.js), pas
   sous `medication`. Les sélecteurs ci-dessous lisaient `state.medication`
   (singulier) : ils renvoyaient tous `undefined`, d'où l'historique des
   médicaments systématiquement vide alors que le GET avait bien répondu.
   `selectMedicationState` s'en sortait grâce à son fallback — d'où la liste
   de gauche qui marchait et l'historique de droite qui restait vide.
   Toutes les lectures passent maintenant par cette base unique. */
const getMedicationState = (state) =>
  state.medications || state.medication || initialState;

const selectMedicationRoot = (state) => getMedicationState(state);

// Selectors
export const selectMedicationReminder = (state) => getMedicationState(state).reminder ?? null;
export const selectMedications = (state) => getMedicationState(state).medications || [];
export const selectActiveMedications = (state) => getMedicationState(state).activeMedications || [];
export const selectMedicationHistory = (state) => getMedicationState(state).medicationHistory || [];
export const selectMedicationLoading = (state) => Boolean(getMedicationState(state).isLoading);
export const selectMedicationError = (state) => getMedicationState(state).errorMessage ?? null;
export const selectArchivedMedications = (state) => getMedicationState(state).archivedMedications || [];

export const selectMedicationStockReminder = (state) =>
  getMedicationState(state).stockReminder;

export const selectMedicationState = createSelector([selectMedicationRoot], (s) => ({
  medications: s.medications || [],
  activeMedications: s.activeMedications || [],
  medicationHistory: s.medicationHistory || [],
  archivedMedications: s.archivedMedications || [],
  reminder: s.reminder || null,
  loading: s.isLoading || false,
  error: s.isError || false,
  errorMessage: s.errorMessage || null,
  stockReminder: s.stockReminder || null,
}));

export default medicationSlice.reducer;
